import React, { useState } from "react";
import { motion } from "framer-motion";
import NavBar from "@/components/NavBar";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  MessageSquare,
  CalendarDays,
  Edit,
  Settings,
  Bell,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  MapPin,
  Globe,
  Award,
  TrendingUp,
  Target,
  Activity,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  useUserProfile,
  useUserCampaigns,
  useUserActivity,
  useUserContributions,
  useUserNotifications,
} from "@/hooks/useApi";
import { useNavigate } from "react-router-dom";

const UserProfile: React.FC = () => {
  const [activeTab, setActiveTab] = useState("projects");
  const { user: authUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  console.log("authUser", authUser);

  // Fetch real user data only if authenticated
  const {
    data: userProfile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useUserProfile();

  const {
    data: userCampaigns = [],
    isLoading: isCampaignsLoading,
    error: campaignsError,
  } = useUserCampaigns();

  const {
    data: userActivity = [],
    isLoading: isActivityLoading,
    error: activityError,
  } = useUserActivity();

  const {
    data: userContributions = [],
    isLoading: isContributionsLoading,
    error: contributionsError,
  } = useUserContributions();

  const { data: userNotifications = [], isLoading: isNotificationsLoading } =
    useUserNotifications();

  console.log("userContributions", userContributions);

  // Show authentication required state
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <NavBar />
        <div className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🔐</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">
                Authentication Required
              </h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Please sign in to view your profile and manage your account.
              </p>
              <Button asChild className="bg-primary hover:bg-primary/90">
                <Link to="/login">Sign In</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isProfileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <NavBar />
        <div className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-20">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-gray-600">Loading your profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (profileError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <NavBar />
        <div className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-20">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">
                Failed to load profile
              </h3>
              <p className="text-gray-600 mb-8">
                {profileError instanceof Error
                  ? profileError.message
                  : "Please try again later."}
              </p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use real user data or fallback to auth user
  const user = {
    ...(userProfile || {
      id: authUser?.id || "",
      name: authUser?.name || "User",
      email: authUser?.email || "",
      username: authUser?.name?.toLowerCase().replace(/\s+/g, "") || "user",
      bio: "Passionate about innovative solutions and validating ideas that solve real problems.",
      location: "Unknown",
      avatarUrl: undefined,
      website: undefined,
      role: authUser?.role || "USER",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
    // Ensure stats are always properly initialized
    stats: {
      campaignsCreated:
        userProfile?.stats?.campaignsCreated ?? userCampaigns.length,
      feedbackGiven: userProfile?.stats?.feedbackGiven ?? 0,
      commentsPosted: userProfile?.stats?.commentsPosted ?? 0,
      totalEngagement: userProfile?.stats?.totalEngagement ?? 0,
    },
  };

  console.log("user", user);

  // Transform campaigns data to match the expected format
  const userProjects = userCampaigns.map((campaign) => ({
    id: campaign.id,
    title: campaign.title,
    description: campaign.description,
    bannerImage: campaign.bannerImage,
    positiveVotes: campaign.positiveVotes,
    negativeVotes: campaign.negativeVotes,
    feedbackCount: campaign.feedbackCount,
    daysLeft: campaign.daysLeft,
    status: campaign.status.toLowerCase(),
  }));

  // Transform activity data
  const userActivityData = userActivity.map((activity) => ({
    id: activity.id,
    type: activity.type,
    description: activity.description,
    timestamp: activity.createdAt,
    campaignId: activity.campaign?.id,
    campaignTitle: activity.campaign?.title,
  }));

  // Calculate unread notifications count
  const unreadNotificationsCount = userNotifications.filter(
    (notification) => !notification.read
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
      <NavBar />
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
        <div className="grid gap-4 sm:gap-6 lg:gap-8 lg:grid-cols-4">
          {/* Enhanced Profile Sidebar */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            {/* Back Button */}
            <motion.div
              className="mb-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-sm">
                <CardContent className="pt-4 sm:pt-6">
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <Avatar className="h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24 ring-2 sm:ring-4 ring-primary/20">
                        <AvatarImage src={user.avatarUrl} alt={user.name} />
                        <AvatarFallback className="bg-gradient-to-r from-primary to-primary-600 text-white text-sm sm:text-base lg:text-lg font-semibold">
                          {user.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-primary to-primary-600 rounded-full flex items-center justify-center">
                        <Award className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                      </div>
                    </div>
                    <h2 className="mt-3 sm:mt-4 text-lg sm:text-xl font-bold text-center">
                      {user.name}
                    </h2>
                    <p className="text-xs sm:text-sm text-primary font-medium text-center">
                      {user.email}
                    </p>

                    <div className="mt-4 sm:mt-6 w-full">
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed text-center">
                        {user.bio}
                      </p>
                    </div>

                    {/* Enhanced stats with gradients */}
                    <div className="mt-4 sm:mt-6 grid grid-cols-3 gap-2 sm:gap-4 w-full">
                      <div className="text-center p-2 sm:p-3 rounded-lg bg-gradient-to-t from-primary/10 to-transparent">
                        <p className="font-bold text-sm sm:text-lg text-primary">
                          {user.stats.campaignsCreated}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Campaigns
                        </p>
                      </div>
                      <div className="text-center p-2 sm:p-3 rounded-lg bg-gradient-to-t from-primary/10 to-transparent">
                        <p className="font-bold text-sm sm:text-lg text-primary">
                          {user.stats.feedbackGiven}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Responses
                        </p>
                      </div>
                      <div className="text-center p-2 sm:p-3 rounded-lg bg-gradient-to-t from-primary/10 to-transparent">
                        <p className="font-bold text-sm sm:text-lg text-primary">
                          {user.stats.commentsPosted}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Comments
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-4 sm:my-6" />

                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm p-2 rounded-lg hover:bg-muted/50 transition-colors">
                      <CalendarDays
                        size={14}
                        className="sm:w-4 sm:h-4 text-primary"
                      />
                      <span>
                        Joined{" "}
                        {new Date(user.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                        })}
                      </span>
                    </div>

                    {user.location && (
                      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm p-2 rounded-lg hover:bg-muted/50 transition-colors">
                        <MapPin
                          size={14}
                          className="sm:w-4 sm:h-4 text-primary"
                        />
                        <span>{user.location}</span>
                      </div>
                    )}

                    {user.website && (
                      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm p-2 rounded-lg hover:bg-muted/50 transition-colors">
                        <Globe
                          size={14}
                          className="sm:w-4 sm:h-4 text-primary"
                        />
                        <a
                          href={user.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline truncate"
                        >
                          {user.website.replace(/^https?:\/\//, "")}
                        </a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4 flex flex-col">
                <Link to="/notifications">
                  <Button
                    variant="outline"
                    className="w-full justify-start hover:bg-primary/10 hover:border-primary/20 transition-all text-xs sm:text-sm"
                  >
                    <Bell
                      size={16}
                      className="sm:w-[18px] sm:h-[18px] mr-2 sm:mr-3 text-primary"
                    />
                    <span className="hidden sm:inline">Notifications</span>
                    <span className="sm:hidden">Notifs</span>
                    <Badge className="ml-auto bg-primary/20 text-primary border-0 text-xs">
                      {unreadNotificationsCount}
                    </Badge>
                  </Button>
                </Link>

                <Link to="/settings">
                  <Button
                    variant="outline"
                    className="w-full justify-start hover:bg-primary/10 hover:border-primary/20 transition-all text-xs sm:text-sm"
                  >
                    <Settings
                      size={16}
                      className="sm:w-[18px] sm:h-[18px] mr-2 sm:mr-3 text-primary"
                    />
                    <span className="hidden sm:inline">Account Settings</span>
                    <span className="sm:hidden">Settings</span>
                    <ChevronRight
                      size={16}
                      className="sm:w-[18px] sm:h-[18px] ml-auto text-muted-foreground"
                    />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Enhanced Main Content */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl rounded-2xl overflow-hidden">
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
                    <TabsList className="w-full bg-transparent border-0 p-0 h-auto rounded-none overflow-x-auto">
                      <TabsTrigger
                        value="projects"
                        className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                      >
                        <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">My Projects</span>
                        <span className="sm:hidden">Projects</span>
                      </TabsTrigger>
                      <TabsTrigger
                        value="contributions"
                        className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                      >
                        <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">
                          My Contributions
                        </span>
                        <span className="sm:hidden">Contributions</span>
                      </TabsTrigger>
                      <TabsTrigger
                        value="activity"
                        className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                      >
                        <Activity className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                        Activity
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="projects" className="p-4 sm:p-6 lg:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                          My Project Ideas
                        </h2>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                          Ideas you've shared with the community
                        </p>
                      </div>
                      <Button
                        asChild
                        className="bg-primary hover:bg-primary/90 text-xs sm:text-sm"
                      >
                        <Link to="/create">
                          <TrendingUp className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">Add New Idea</span>
                          <span className="sm:hidden">New Idea</span>
                        </Link>
                      </Button>
                    </div>

                    {isCampaignsLoading ? (
                      <div className="text-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                        <p className="text-gray-600">
                          Loading your campaigns...
                        </p>
                      </div>
                    ) : userProjects.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">🚀</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          No campaigns yet
                        </h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                          Start your journey by creating your first campaign and
                          sharing your innovative ideas with the community.
                        </p>
                        <Button
                          asChild
                          className="bg-primary hover:bg-primary/90"
                        >
                          <Link to="/create">
                            <TrendingUp className="mr-2 h-4 w-4" />
                            Create Your First Campaign
                          </Link>
                        </Button>
                      </div>
                    ) : (
                      <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2">
                        {userProjects.map((project, index) => (
                          <motion.div
                            key={project.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                          >
                            <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card/50 backdrop-blur-sm group">
                              <AspectRatio ratio={16 / 9}>
                                <img
                                  src={project.bannerImage || ""}
                                  alt={project.title}
                                  className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                                />
                              </AspectRatio>
                              <CardHeader className="pb-2">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-0">
                                  <CardTitle className="text-sm sm:text-base lg:text-lg hover:text-primary transition-colors">
                                    {project.title}
                                  </CardTitle>
                                  <Badge
                                    variant={
                                      project.status === "active"
                                        ? "default"
                                        : project.status === "completed"
                                        ? "secondary"
                                        : "outline"
                                    }
                                    className={`w-fit text-xs sm:text-sm ${
                                      project.status === "active"
                                        ? "bg-gradient-to-r from-primary to-primary-600 text-white"
                                        : project.status === "completed"
                                        ? "bg-gradient-to-r from-primary/80 to-primary-600 text-white"
                                        : ""
                                    }`}
                                  >
                                    {project.status}
                                  </Badge>
                                </div>
                                <CardDescription className="line-clamp-2 text-xs sm:text-sm">
                                  {project.description}
                                </CardDescription>
                              </CardHeader>
                              <CardContent className="pb-2">
                                {project.status !== "draft" && (
                                  <div className="flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-0 text-xs sm:text-sm text-muted-foreground">
                                    <div className="flex gap-3 sm:gap-4">
                                      <div className="flex items-center hover:text-primary transition-colors">
                                        <ThumbsUp
                                          size={12}
                                          className="sm:w-[14px] sm:h-[14px] mr-1"
                                        />
                                        {project.positiveVotes}
                                      </div>
                                      <div className="flex items-center hover:text-primary transition-colors">
                                        <ThumbsDown
                                          size={12}
                                          className="sm:w-[14px] sm:h-[14px] mr-1"
                                        />
                                        {project.negativeVotes}
                                      </div>
                                    </div>
                                    <div className="flex items-center hover:text-primary transition-colors">
                                      <MessageSquare
                                        size={12}
                                        className="sm:w-[14px] sm:h-[14px] mr-1"
                                      />
                                      {project.feedbackCount} feedback
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                              <CardFooter>
                                <div className="flex w-full gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 hover:bg-primary/10 hover:border-primary/20 text-xs sm:text-sm"
                                    asChild
                                  >
                                    <Link to={`/campaign/${project.id}`}>
                                      View
                                    </Link>
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="flex-1 bg-primary hover:bg-primary/90 text-xs sm:text-sm"
                                    asChild
                                  >
                                    <Link to={`/edit-campaign/${project.id}`}>
                                      <Edit
                                        size={14}
                                        className="sm:w-4 sm:h-4 mr-1"
                                      />
                                      <span className="hidden sm:inline">
                                        {project.status === "draft"
                                          ? "Edit Draft"
                                          : "Manage"}
                                      </span>
                                      <span className="sm:hidden">
                                        {project.status === "draft"
                                          ? "Edit"
                                          : "Manage"}
                                      </span>
                                    </Link>
                                  </Button>
                                </div>
                              </CardFooter>
                            </Card>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent
                    value="contributions"
                    className="p-4 sm:p-6 lg:p-8"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                          My Contributions
                        </h2>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                          Campaigns you've contributed to through comments,
                          feedback, and interactions
                        </p>
                      </div>
                      <Button
                        asChild
                        variant="outline"
                        className="text-xs sm:text-sm"
                      >
                        <Link to="/browse">
                          <MessageSquare className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">
                            Browse More Campaigns
                          </span>
                          <span className="sm:hidden">Browse</span>
                        </Link>
                      </Button>
                    </div>

                    {isContributionsLoading ? (
                      <div className="text-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                        <p className="text-gray-600">
                          Loading your contributions...
                        </p>
                      </div>
                    ) : contributionsError ? (
                      <div className="text-center py-12">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          Failed to load contributions
                        </h3>
                        <p className="text-gray-600 mb-6">
                          {contributionsError instanceof Error
                            ? contributionsError.message
                            : "Please try again later."}
                        </p>
                        <Button onClick={() => window.location.reload()}>
                          Retry
                        </Button>
                      </div>
                    ) : userContributions.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">💬</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          No contributions yet
                        </h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                          Start contributing to campaigns by commenting,
                          providing feedback, or giving Bloom Scores to see your
                          contributions here.
                        </p>
                        <Button
                          asChild
                          className="bg-primary hover:bg-primary/90"
                        >
                          <Link to="/browse">
                            <MessageSquare className="mr-2 h-4 w-4" />
                            Explore Campaigns
                          </Link>
                        </Button>
                      </div>
                    ) : (
                      <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2">
                        {userContributions.map((contribution, index) => (
                          <motion.div
                            key={contribution.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                          >
                            <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card/50 backdrop-blur-sm group">
                              <AspectRatio ratio={16 / 9}>
                                <img
                                  src={contribution.bannerImage}
                                  alt={contribution.title}
                                  className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                                />
                              </AspectRatio>
                              <CardHeader className="pb-2">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-0">
                                  <CardTitle className="text-sm sm:text-base lg:text-lg hover:text-primary transition-colors">
                                    {contribution.title}
                                  </CardTitle>
                                  <div className="flex items-center gap-1 sm:gap-2">
                                    {contribution.hasBothContributions && (
                                      <Badge
                                        variant="outline"
                                        className="bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200 text-xs"
                                      >
                                        📝 + 💬
                                      </Badge>
                                    )}
                                    <Badge
                                      variant="outline"
                                      className="bg-gradient-to-r from-primary/10 to-primary/20 text-primary border-primary/30 text-xs sm:text-sm"
                                    >
                                      {contribution.contributionType ===
                                        "comment" && "💬 Comment"}
                                      {contribution.contributionType ===
                                        "feedback" && "📝 Feedback"}
                                      {contribution.contributionType ===
                                        "bloom_score" && "🌱 Bloom Score"}
                                      {contribution.contributionType ===
                                        "response" && "📋 Response"}
                                    </Badge>
                                  </div>
                                </div>
                                <CardDescription className="line-clamp-2 text-xs sm:text-sm">
                                  {contribution.description}
                                </CardDescription>
                              </CardHeader>
                              <CardContent className="pb-2">
                                {/* Contribution details */}
                                <div className="mb-3 p-2 sm:p-3 bg-muted/30 rounded-lg">
                                  <div className="flex items-start gap-2">
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                      {contribution.contributionType ===
                                        "comment" && (
                                        <MessageSquare className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                                      )}
                                      {contribution.contributionType ===
                                        "feedback" && (
                                        <ThumbsUp className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                                      )}
                                      {contribution.contributionType ===
                                        "bloom_score" && (
                                        <Award className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                                      )}
                                      {contribution.contributionType ===
                                        "response" && (
                                        <MessageSquare className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs sm:text-sm font-medium text-gray-900 mb-1">
                                        Your{" "}
                                        {contribution.contributionType ===
                                        "comment"
                                          ? "Comment"
                                          : contribution.contributionType ===
                                            "feedback"
                                          ? "Feedback"
                                          : contribution.contributionType ===
                                            "bloom_score"
                                          ? "Bloom Score"
                                          : "Response"}
                                        {contribution.hasBothContributions && (
                                          <span className="text-emerald-600 ml-1">
                                            {" "}
                                            + Feedback
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-600 line-clamp-2">
                                        {contribution.contributionText}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {new Date(
                                          contribution.timestamp
                                        ).toLocaleDateString("en-US", {
                                          year: "numeric",
                                          month: "short",
                                          day: "numeric",
                                        })}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* Campaign stats */}
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-0 text-xs sm:text-sm text-muted-foreground">
                                  <div className="flex gap-3 sm:gap-4">
                                    <div className="flex items-center hover:text-primary transition-colors">
                                      <ThumbsUp
                                        size={12}
                                        className="sm:w-[14px] sm:h-[14px] mr-1"
                                      />
                                      {contribution.positiveVotes}
                                    </div>
                                    <div className="flex items-center hover:text-primary transition-colors">
                                      <ThumbsDown
                                        size={12}
                                        className="sm:w-[14px] sm:h-[14px] mr-1"
                                      />
                                      {contribution.negativeVotes}
                                    </div>
                                  </div>
                                  <div className="flex items-center hover:text-primary transition-colors">
                                    <MessageSquare
                                      size={12}
                                      className="sm:w-[14px] sm:h-[14px] mr-1"
                                    />
                                    {contribution.feedbackCount} feedback
                                  </div>
                                </div>
                              </CardContent>
                              <CardFooter>
                                <div className="flex w-full gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 hover:bg-primary/10 hover:border-primary/20 text-xs sm:text-sm"
                                    asChild
                                  >
                                    <Link
                                      to={`/campaign/${contribution.campaignId}`}
                                    >
                                      <span className="hidden sm:inline">
                                        View Campaign
                                      </span>
                                      <span className="sm:hidden">View</span>
                                    </Link>
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="flex-1 bg-primary hover:bg-primary/90 text-xs sm:text-sm"
                                    asChild
                                  >
                                    <Link
                                      to={`/campaign/${contribution.campaignId}`}
                                    >
                                      <MessageSquare
                                        size={14}
                                        className="sm:w-4 sm:h-4 mr-1"
                                      />
                                      <span className="hidden sm:inline">
                                        Contribute More
                                      </span>
                                      <span className="sm:hidden">More</span>
                                    </Link>
                                  </Button>
                                </div>
                              </CardFooter>
                            </Card>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="activity" className="p-4 sm:p-6 lg:p-8">
                    <div className="mb-4 sm:mb-6">
                      <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                        Recent Activity
                      </h2>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        Your recent interactions and contributions
                      </p>
                    </div>

                    {isActivityLoading ? (
                      <div className="text-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                        <p className="text-gray-600">
                          Loading your activity...
                        </p>
                      </div>
                    ) : userActivityData.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📊</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          No activity yet
                        </h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                          Start engaging with campaigns to see your activity
                          here.
                        </p>
                        <Button asChild variant="outline">
                          <Link to="/browse">Browse Campaigns</Link>
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3 sm:space-y-4">
                        {userActivityData.map((activity, index) => (
                          <motion.div
                            key={activity.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-primary to-primary-600 flex items-center justify-center flex-shrink-0">
                              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm font-medium text-gray-900">
                                {activity.description}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(
                                  activity.timestamp
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
